/**
 * DOME to OPP Redirect Component
 * 
 * This component checks if the user is accessing via the old DOME domain
 * and redirects them to the new OPP domain with a branded message.
 * 
 * TEMPORARY: Remove this file after the transition period (~1 month)
 */

import React, { useEffect, useState } from 'react';

// Configuration - Update these values as needed
const REDIRECT_CONFIG = {
    // Domains that should be redirected (old DOME domains)
    redirectFromPatterns: ['dome'],
    // Countdown seconds before auto-redirect
    countdownSeconds: 10,
    // Enable/disable the redirect (can be controlled via env variable)
    enabled: true
};

// Get the target OPP URL based on current environment
const getTargetBaseUrl = (): string => {
    const currentHost = window.location.hostname.toLowerCase();
    
    // Map DOME domains to OPP domains
    if (currentHost.includes('dome-uat')) {
        return 'https://opp-uat.microsoft.com';
    } else if (currentHost.includes('dome-dev') || currentHost.includes('dome-test')) {
        return 'https://opp-dev.microsoft.com';
    } else if (currentHost.includes('dome.microsoft.com') || currentHost.includes('dome-prod')) {
        return 'https://opp.microsoft.com';
    }
    
    // Default: replace 'dome' with 'opp' in the current hostname
    const newHost = currentHost.replace('dome', 'opp');
    return `https://${newHost}`;
};

// Get the bookmark URL to display based on environment
const getBookmarkUrl = (): { url: string; display: string } => {
    const currentHost = window.location.hostname.toLowerCase();
    
    if (currentHost.includes('dome-uat')) {
        return { url: 'https://opp-uat.microsoft.com', display: 'https://opp-uat.microsoft.com' };
    }
    
    // Production uses aka.ms shortcut
    return { url: 'https://aka.ms/opp', display: 'https://aka.ms/opp' };
};

// Check if current domain should redirect
const shouldRedirect = (): boolean => {
    if (!REDIRECT_CONFIG.enabled) return false;
    const currentHost = window.location.hostname.toLowerCase();
    return REDIRECT_CONFIG.redirectFromPatterns.some(pattern => 
        currentHost.includes(pattern.toLowerCase())
    );
};

// Get the target URL preserving path and query
const getTargetUrl = (): string => {
    const { pathname, search, hash } = window.location;
    return `${getTargetBaseUrl()}${pathname}${search}${hash}`;
};

// Styles for the redirect page
const styles: { [key: string]: React.CSSProperties } = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#0078d4',
        background: 'linear-gradient(135deg, #0078d4 0%, #005a9e 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 99999,
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        padding: '20px',
        boxSizing: 'border-box'
    },
    container: {
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '40px 50px',
        maxWidth: '600px',
        textAlign: 'center' as const,
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
    },
    logoContainer: {
        marginBottom: '30px'
    },
    logoTransition: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '20px',
        flexWrap: 'wrap' as const
    },
    logoBoxOld: {
        padding: '15px 25px',
        borderRadius: '8px',
        fontSize: '24px',
        fontWeight: 'bold',
        backgroundColor: '#f0f0f0',
        color: '#666',
        textDecoration: 'line-through'
    },
    arrow: {
        fontSize: '30px',
        color: '#0078d4'
    },
    logoBoxNew: {
        padding: '15px 25px',
        borderRadius: '8px',
        fontSize: '24px',
        fontWeight: 'bold',
        backgroundColor: '#0078d4',
        color: 'white'
    },
    title: {
        color: '#0078d4',
        marginBottom: '20px',
        fontSize: '28px',
        margin: '0 0 20px 0'
    },
    message: {
        color: '#333',
        fontSize: '16px',
        lineHeight: '1.6',
        marginBottom: '25px'
    },
    highlight: {
        backgroundColor: '#fff3cd',
        borderLeft: '4px solid #ffc107',
        padding: '15px 20px',
        margin: '20px 0',
        textAlign: 'left' as const,
        borderRadius: '0 8px 8px 0'
    },
    highlightStrong: {
        color: '#856404'
    },
    countdown: {
        color: '#666',
        fontSize: '14px',
        marginBottom: '20px'
    },
    countdownNumber: {
        fontWeight: 'bold',
        color: '#0078d4',
        fontSize: '18px'
    },
    button: {
        display: 'inline-block',
        backgroundColor: '#0078d4',
        color: 'white',
        padding: '12px 30px',
        borderRadius: '6px',
        textDecoration: 'none',
        fontWeight: 600,
        border: 'none',
        cursor: 'pointer',
        fontSize: '16px',
        transition: 'background 0.3s'
    },
    footer: {
        marginTop: '30px',
        color: '#999',
        fontSize: '12px'
    },
    link: {
        color: '#0078d4'
    }
};

interface DomeToOppRedirectProps {
    children: React.ReactNode;
}

export const DomeToOppRedirect: React.FC<DomeToOppRedirectProps> = ({ children }) => {
    const [countdown, setCountdown] = useState(REDIRECT_CONFIG.countdownSeconds);
    const [showRedirect, setShowRedirect] = useState(false);
    const bookmarkUrl = getBookmarkUrl();

    useEffect(() => {
        // Check if we should show the redirect page
        if (shouldRedirect()) {
            setShowRedirect(true);
        }
    }, []);

    useEffect(() => {
        if (!showRedirect) return;

        // Countdown timer
        const timer = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    window.location.href = getTargetUrl();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [showRedirect]);

    const handleRedirectNow = () => {
        window.location.href = getTargetUrl();
    };

    // If not on DOME domain, render the app normally
    if (!showRedirect) {
        return <>{children}</>;
    }

    // Show the redirect page
    return (
        <div style={styles.overlay}>
            <div style={styles.container}>
                <div style={styles.logoContainer}>
                    <div style={styles.logoTransition}>
                        <div style={styles.logoBoxOld}>DOME</div>
                        <div style={styles.arrow}>→</div>
                        <div style={styles.logoBoxNew}>OPP</div>
                    </div>
                </div>

                <h1 style={styles.title}>We've Got a New Name!</h1>

                <p style={styles.message}>
                    <strong>DOME</strong> has been rebranded to{' '}
                    <strong>OPP</strong> (Offer Publishing).
                </p>

                <div style={styles.highlight}>
                    <strong style={styles.highlightStrong}>📢 What's changing?</strong><br />
                    Only the name and URL are changing. All your data and functionality remain the same.
                    Please update your bookmarks to the new URL{' '}
                    <a href={bookmarkUrl.url} target="_blank" rel="noopener noreferrer" style={styles.link}>
                        {bookmarkUrl.display}
                    </a>.
                </div>

                <p style={styles.countdown}>
                    Redirecting you automatically in{' '}
                    <span style={styles.countdownNumber}>{countdown}</span> seconds...
                </p>

                <button 
                    style={styles.button} 
                    onClick={handleRedirectNow}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#005a9e'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#0078d4'}
                >
                    Go to OPP Now →
                </button>

                <div style={styles.footer}>
                    <p>If you have any questions, please contact the OPP support team.</p>
                </div>
            </div>
        </div>
    );
};

export default DomeToOppRedirect;
